import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Truck, Package, Home } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Services = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 animate-fadeInUp">
            Moving Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 animate-fadeInUp animation-delay-100">
            Professional moving solutions tailored to your needs
          </p>
        </div>
      </section>

      {/* Featured: Long Distance Moving */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden animate-scaleIn">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4 w-fit">
                  <Truck size={16} />
                  <span>FEATURED SERVICE</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Long Distance Moving
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Moving across BC or beyond? We specialize in long-distance relocations with meticulous planning and care. Your belongings are tracked, protected, and delivered safely to your new home.
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">GPS tracking for peace of mind</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Secure packaging designed for long trips</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Detailed inventory management</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Flexible scheduling to meet your timeline</span>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105 w-fit"
                >
                  Get Your Free Quote
                </Link>
              </div>
              <div className="relative h-64 lg:h-auto bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                <Truck size={120} className="text-white opacity-20" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white">
                    <p className="text-6xl font-bold mb-2">500+</p>
                    <p className="text-xl">Long Distance Moves</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Local Moving */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 hover:shadow-xl hover:border-orange-200 transition-all duration-300 animate-fadeInUp">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Local Moving
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Professional moving services within your city or nearby areas. We handle everything from small apartments to large homes.
              </p>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What's included:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Professional packing and unpacking</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Furniture disassembly and reassembly</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Loading and unloading</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Safe transportation of all items</span>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
              >
                Request a Quote
              </Link>
            </div>

            {/* Packing & Unpacking */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 hover:shadow-xl hover:border-orange-200 transition-all duration-300 animate-fadeInUp animation-delay-100">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Packing & Unpacking
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Let our experienced team handle the packing. We use quality materials to protect your belongings during the move.
              </p>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What's included:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Professional packing materials provided</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Careful wrapping of fragile items</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Labeled boxes for easy unpacking</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Full unpacking service available</span>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
              >
                Request a Quote
              </Link>
            </div>

            {/* Furniture Pickup & Delivery */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 hover:shadow-xl hover:border-orange-200 transition-all duration-300 animate-fadeInUp animation-delay-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Furniture Pickup & Delivery
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Need to move just a few items? We offer single-item and furniture delivery services for your convenience.
              </p>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What's included:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Same-day or scheduled delivery</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Furniture protection and handling</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Assembly service available</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Commercial and residential</span>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
              >
                Request a Quote
              </Link>
            </div>

            {/* Last-Mile / Business Deliveries */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 hover:shadow-xl hover:border-orange-200 transition-all duration-300 animate-fadeInUp animation-delay-300">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Last-Mile / Business Deliveries
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Professional delivery services for businesses and retailers. We ensure your products reach customers safely and on time.
              </p>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What's included:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Scheduled delivery windows</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Professional handling</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Direct customer communication</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-2 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Proof of delivery documentation</span>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center animate-fadeInUp">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <details className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 animate-fadeInUp">
              <summary className="font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors">Are you insured?</summary>
              <p className="mt-3 text-gray-600">Yes, we are fully licensed and insured. Your belongings are protected throughout the entire moving process.</p>
            </details>
            <details className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 animate-fadeInUp animation-delay-100">
              <summary className="font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors">Do you provide wrapping supplies?</summary>
              <p className="mt-3 text-gray-600">Yes, we provide all necessary packing materials including boxes, bubble wrap, blankets, and protective padding for your items.</p>
            </details>
            <details className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 animate-fadeInUp animation-delay-200">
              <summary className="font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors">How far ahead should I book?</summary>
              <p className="mt-3 text-gray-600">We recommend booking at least 1-2 weeks in advance, especially during peak moving season (May-September). However, we can often accommodate last-minute moves.</p>
            </details>
            <details className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 animate-fadeInUp animation-delay-300">
              <summary className="font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors">How do quotes work?</summary>
              <p className="mt-3 text-gray-600">We provide free, no-obligation quotes based on your specific needs. Simply fill out our quote form or give us a call, and we will provide a clear, transparent estimate.</p>
            </details>
            <details className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 animate-fadeInUp animation-delay-400">
              <summary className="font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors">What payment methods do you accept?</summary>
              <p className="mt-3 text-gray-600">We accept cash, credit cards, debit cards, and e-transfer. Payment is typically due upon completion of the move.</p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Request your free quote today
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
          >
            Get a Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
